import {Injectable} from '@angular/core';
import {NgxIndexedDBService} from 'ngx-indexed-db';
import {lastValueFrom} from "rxjs";
import moment from 'moment';
import {SdkService} from './sdk.service';

@Injectable({
  providedIn: 'root'
})
export class CacheService {

  cacheConfig: any;
  cacheName: string = 'fleet_api_cache';
  private inProgressRequests = new Map<string, Promise<any>>();

  constructor(private dbService: NgxIndexedDBService,
              private sdkService: SdkService
  ) {

  }

  private generateCacheKey(url: string, params: any): string {
    const serializedParams = JSON.stringify(params);
    return `${url}?params=${serializedParams}`;
  }

  async getFromCache(url: string, params: any): Promise<any> {
    const cacheKey = this.generateCacheKey(url, params);
    return this.dbService.getByIndex(this.cacheName, 'url', cacheKey).toPromise();
  }

  async deleteItem(url: any, params: any): Promise<void> {
    this.dbService.deleteByKey(this.cacheName, this.generateCacheKey(url, params));
  }

  async clearCache(): Promise<any> {
    const $clearedDB = this.dbService.clear(this.cacheName);
    return await lastValueFrom($clearedDB);
  }

  async saveToCache(url: string, params: any, response: any, tags?: string | string[]): Promise<void> {
    const cacheKey = this.generateCacheKey(url, params);
    const responseTime = moment().format('DD/MM/YYYY HH:mm:ss');
    const stringResponse = JSON.stringify(response);

    const tagsArray = tags ? (Array.isArray(tags) ? tags : [tags]) : null;

    const cacheItem = {
      url: cacheKey,
      stringResponse,
      responseTime,
      tags: tagsArray
    };

    if (await this.getFromCache(url, params)) {
      await this.dbService.update(this.cacheName, cacheItem).toPromise();
    } else {
      await this.dbService.add(this.cacheName, cacheItem).toPromise();
    }
  }

  async get(action: string, params: any = {}, showError = true, tags?: string | string[]): Promise<any> {
    const cacheKey = this.generateCacheKey(action, params);
    if (this.inProgressRequests.has(cacheKey)) {
      console.log('MULTIPLE CALL DETECTED: ', cacheKey);
      return this.inProgressRequests.get(cacheKey)!;
    }

    const requestPromise = (async () => {
      const cachedResponse = await this.getFromCache(action, params);
      if (this.cacheConfig['ACTIVE'] && cachedResponse && this.checkIfResponseIsOutdated(cachedResponse)) {
        console.log(`Cache hit for ${action} with params. Stored response:`, JSON.parse(cachedResponse.stringResponse));
        return JSON.parse(cachedResponse.stringResponse);
      } else {
        console.log(`Cache miss for ${action} with params`);
        const response = await this.sdkService.get(action, params, showError);
        if (!response?.errors) {
          await this.saveToCache(action, params, response, tags);
        }
        return response;
      }
    })();

    this.inProgressRequests.set(cacheKey, requestPromise);
    try {
      return await requestPromise;
    } finally {
      this.inProgressRequests.delete(cacheKey);
    }
  }

  async set(action: string, params: any = {}, showError = true, tags?: string | string[]): Promise<any> {
    const cacheKey = this.generateCacheKey(action, params);
    if (this.inProgressRequests.has(cacheKey)) {
      console.log('MULTIPLE CALL DETECTED: ', cacheKey);
      return this.inProgressRequests.get(cacheKey)!;
    }

    const requestPromise = (async () => {
      const cachedResponse = await this.getFromCache(action, params);
      if (this.cacheConfig['ACTIVE'] && cachedResponse && this.checkIfResponseIsOutdated(cachedResponse)) {
        console.log(`Cache hit for ${action} with params. Stored response:`, JSON.parse(cachedResponse.stringResponse));
        return JSON.parse(cachedResponse.stringResponse);
      } else {
        console.log(`Cache miss for ${action} with params`);
        const response = await this.sdkService.set(action, params, showError);
        if (!response?.errors) {
          await this.saveToCache(action, params, response, tags);
        }
        return response;
      }
    })();

    this.inProgressRequests.set(cacheKey, requestPromise);
    try {
      return await requestPromise;
    } finally {
      this.inProgressRequests.delete(cacheKey);
    }
  }

  checkIfResponseIsOutdated(response: any): boolean {
    console.log('CACHE => ', moment().format('DD/MM/YYYY HH:mm:ss'), moment(response.responseTime, 'DD/MM/YYYY HH:mm:ss').format('DD/MM/YYYY HH:mm:ss'));
    const difference = moment().diff(moment(response.responseTime, 'DD/MM/YYYY HH:mm:ss'), 's');
    console.log('CACHE => ', 'Difference in time:', Math.abs(difference), ' seconds');
    return difference < this.cacheConfig['TTL'];
  }

  setCacheConfig(config: any): void {
    this.cacheConfig = config;
  }

  async deleteByTag(tags: string | string[]): Promise<void> {
    const tagsArray = Array.isArray(tags) ? tags : [tags];

    const allItems = await lastValueFrom(this.dbService.getAll(this.cacheName)) as Array<{ url: string, tags?: string[] }>;
    const itemsToDelete = allItems.filter(item =>
      item.tags && item.tags.some((tag: string) => tagsArray.includes(tag))
    );

    console.log('Cache API delete - Found tags: ', itemsToDelete)
    console.log('Cache API delete - URLs to delete: ', itemsToDelete.map(item => item.url));
    for (const item of itemsToDelete) {
      console.log('Cache API delete - URLs to delete: ', item.url);
      await this.dbService.deleteByKey(this.cacheName, item.url).subscribe({
        next: (result) => {
          console.log('Cache API delete - Result: ',result);
        }
      });
    }
  }
}
